import type { Tone } from '../../ui/v2';
import { api, apiEndpoints } from '../../utils/apiService';

export const RESERVATIONS_PAGE_SIZE = 25;
export const DEFAULT_OPENTABLE_ORDERING = '-updated_at';
export const DEFAULT_LINKS_ORDERING = '-created_at';

export type OpenTableReservation = {
  updated_at?: string | null;
  status?: string | null;
  confirmation_code?: string | null;
  reservation_id?: string | number | null;
  customer_id?: string | number | null;
  start_time?: string | null;
  party_size?: number | string | null;
  guest_first_name?: string | null;
  guest_last_name?: string | null;
  guest_phone_number?: string | null;
  guest_email?: string | null;
  is_vip?: boolean;
  vip_name?: string | null;
  special_request?: string | null;
  created_via?: string | null;
  origin_channel?: string | null;
  deposit?:
    | number
    | string
    | { amount_dollars?: number | string | null }
    | null;
  table_type?: string | null;
};

export interface OpenTableReservationsQueryResult {
  reservations: OpenTableReservation[];
  totalPages: number;
  totalCount: number;
  averagePartySizeDisplay: string;
}

export interface ReservationLink {
  datetime: string;
  customer_id: number;
  customer_name: string;
  customer_phone: string;
  reservation_link: string;
  created_at: string;
}

export interface ReservationLinksQueryResult {
  reservations: ReservationLink[];
  totalCount: number;
}

/** UI column key -> API `ordering` field name. */
export const OPENTABLE_SORT_UI_TO_FIELD: Record<string, string> = {
  updated_at: 'updated_at',
  start_time: 'start_time',
  status: 'status',
  customer_id: 'customer_id',
};

export const OPENTABLE_SORT_FIELD_TO_UI: Record<string, string> =
  Object.fromEntries(
    Object.entries(OPENTABLE_SORT_UI_TO_FIELD).map(([k, v]) => [v, k]),
  );

export const LINKS_SORT_UI_TO_FIELD: Record<string, string> = {
  created_at: 'created_at',
  customer_id: 'customer_id',
  customer_name: 'customer_name',
};

export const LINKS_SORT_FIELD_TO_UI: Record<string, string> =
  Object.fromEntries(
    Object.entries(LINKS_SORT_UI_TO_FIELD).map(([k, v]) => [v, k]),
  );

export function averagePartySizeLabel(
  data: unknown,
  list: OpenTableReservation[],
): string {
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    const o = data as Record<string, unknown>;
    const raw = o.average_party_size ?? o.avg_party_size;
    if (raw != null && raw !== '') {
      const n = typeof raw === 'number' ? raw : Number(String(raw));
      if (!Number.isNaN(n) && Number.isFinite(n)) {
        return n % 1 === 0 ? String(Math.round(n)) : n.toFixed(1);
      }
    }
  }
  const sizes = list
    .map((r) => {
      const p = r.party_size;
      if (p == null || p === '') return null;
      const n = typeof p === 'number' ? p : Number(String(p));
      return Number.isNaN(n) ? null : n;
    })
    .filter((x): x is number => x != null);
  if (sizes.length === 0) return '—';
  const avg = sizes.reduce((a, b) => a + b, 0) / sizes.length;
  return avg % 1 === 0 ? String(Math.round(avg)) : avg.toFixed(1);
}

export async function fetchOpenTableReservationsPage(
  currentPage: number,
  ordering: string,
  search: string,
): Promise<OpenTableReservationsQueryResult> {
  const params = new URLSearchParams({
    page: String(currentPage),
    page_size: String(RESERVATIONS_PAGE_SIZE),
    ordering,
  });
  if (search) {
    params.set('q', search);
  }
  const response = await api.get(
    `${apiEndpoints.openTableDashboardReservations()}?${params.toString()}`,
  );

  if (!response) {
    throw new Error('Failed to fetch OpenTable reservations.');
  }

  if (!response.ok) {
    const text = await response.text();
    throw new Error(
      `Failed to fetch OpenTable reservations${
        response.status ? ` (status ${response.status})` : ''
      }. ${text.substring(0, 120)}`,
    );
  }

  const data = await response.json();
  let reservationsList: OpenTableReservation[] = [];
  let totalCount = 0;
  let totalPages = 1;

  if (Array.isArray(data)) {
    reservationsList = data;
    totalCount = data.length || 0;
  } else if (Array.isArray(data?.results)) {
    reservationsList = data.results;
    totalCount = data.count || reservationsList.length || 0;
    totalPages =
      data.total_pages ||
      Math.max(1, Math.ceil(totalCount / RESERVATIONS_PAGE_SIZE));
  } else {
    console.warn(
      'Unexpected response format for OpenTable reservations:',
      data,
    );
  }

  return {
    reservations: reservationsList,
    totalCount,
    totalPages,
    averagePartySizeDisplay: averagePartySizeLabel(data, reservationsList),
  };
}

export async function fetchReservationLinksPage(
  currentPage: number,
  itemsPerPage: number,
  ordering?: string,
): Promise<ReservationLinksQueryResult> {
  const response = await api.get(
    apiEndpoints.getReservations(currentPage, itemsPerPage, ordering),
  );

  if (!response?.ok) {
    throw new Error('Failed to fetch reservations');
  }

  const data = await response.json();
  let reservationsList: ReservationLink[] = [];
  let totalCount = 0;

  if (data.results && Array.isArray(data.results)) {
    reservationsList = data.results;
    totalCount = data.count || 0;
  } else if (Array.isArray(data)) {
    reservationsList = data;
    totalCount = data.length;
  } else {
    console.error('Unexpected response format:', data);
  }

  return { reservations: reservationsList, totalCount };
}

/** OpenTable status words are free text; match on stems, first match wins. */
export const openTableStatusTone = (status?: string | null): Tone => {
  const s = (status || '').toLowerCase();
  if (s.includes('confirm')) return 'success';
  if (s.includes('cancel') || s.includes('cancell')) return 'danger';
  // Warm: the booking needs a second look — it is waiting, or it changed.
  if (
    s.includes('pending') ||
    s.includes('hold') ||
    s.includes('wait') ||
    s.includes('modif')
  ) {
    return 'warning';
  }
  if (s.includes('seat') || s.includes('arriv')) return 'accent';
  return 'neutral';
};

/** "CONFIRMED" / "no_show" → "Confirmed" / "No show" for display. */
export const sentenceCaseStatus = (status: string): string => {
  const words = status.replace(/[_-]+/g, ' ').trim().toLowerCase();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : status;
};

export const formatGuestName = (reservation: OpenTableReservation) =>
  [reservation.guest_first_name, reservation.guest_last_name]
    .filter(Boolean)
    .join(' ')
    .trim();

export const reservationOrigin = (origin?: string | null) => {
  if (origin === 'EXTERNAL_OPENTABLE') {
    return {
      label: 'OpenTable',
      title: 'Booked outside Arthur; later edited by voice',
      // Neutral: source is provenance, not a state that needs attention.
      tone: 'neutral' as const,
    };
  }
  if (origin === 'ARTHUR_VOICE') {
    return {
      label: 'Arthur',
      title: 'Created by Arthur voice',
      tone: 'accent' as const,
    };
  }
  return null;
};

export function parseOrdering(
  ordering: string,
  fieldToUi: Record<string, string>,
  fallback: string,
) {
  const desc = ordering.startsWith('-');
  const field = desc ? ordering.slice(1) : ordering;
  return {
    sortKey: fieldToUi[field] ?? fallback,
    sortOrder: (desc ? 'desc' : 'asc') as 'asc' | 'desc',
  };
}

/** Next `ordering` value: a new field starts ascending, the same one flips. */
export function nextOrdering(previous: string, targetField: string): string {
  const prevDesc = previous.startsWith('-');
  const prevField = prevDesc ? previous.slice(1) : previous;
  const newDesc = prevField !== targetField ? false : !prevDesc;
  return newDesc ? `-${targetField}` : targetField;
}
